import { ChevronDown, Menu, ShoppingBag } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useBasket } from "@/context/BasketContext";
import { useSite } from "@/context/SiteContext";
import { imgUrl } from "@/lib/api";

function Logo({ compact }) {
  const { settings } = useSite();
  const branding = settings?.branding || {};
  const business = settings?.business || {};
  const logo = branding.logo_light || branding.logo_dark;
  if (logo) {
    return (
      <span className={`inline-flex rounded-full bg-[#54524e] shadow-sm transition-all duration-300 ${compact ? "p-1" : "p-1.5 sm:p-2"}`} data-testid="site-logo">
        <img
          src={imgUrl(logo)}
          alt={business.name || "Eileen Jenkins Florist"}
          className={`object-contain rounded-full transition-all duration-300 ${compact ? "h-9 w-9" : "h-12 w-12 sm:h-16 sm:w-16"}`}
        />
      </span>
    );
  }
  return null;
}

function CategoryDropdown({ parent }) {
  return (
    <div className="relative group" data-testid={`nav-dropdown-${parent.slug}`}>
      <Link
        to={`/shop?category=${parent.slug}`}
        data-testid={`nav-link-${parent.slug}`}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors"
      >
        {parent.name}
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
      </Link>
      <div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50">
        <div className="w-56 rounded-xl border bg-popover shadow-lg p-2 animate-in fade-in-0 zoom-in-95">
          <Link to={`/shop?category=${parent.slug}`} className="block px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors" data-testid={`nav-sub-all-${parent.slug}`}>
            All {parent.name}
          </Link>
          {parent.children.map((child) => (
            <Link key={child.id} to={`/shop?category=${child.slug}`} className="block px-3 py-2 rounded-lg text-sm text-foreground/80 hover:bg-muted hover:text-primary transition-colors" data-testid={`nav-sub-${child.slug}`}>
              {child.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const { count } = useBasket();
  const { categoryTree, settings } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const [spacerH, setSpacerH] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev ? y > 25 : y > 90));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      if (window.scrollY <= 25) setSpacerH(el.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const business = settings?.business || {};
  const badge = settings?.branding?.accreditation_badge;

  const simpleLinkCls = ({ isActive }) =>
    `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
      isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:text-primary hover:bg-primary/5"
    }`;

  const mobileGo = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-40 border-b bg-background/85 backdrop-blur-xl" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4">
        {/* Brand row: logo — name across the top — GFG badge */}
        <div className={`flex items-center gap-3 sm:gap-4 transition-all duration-300 ${scrolled ? "py-1.5" : "py-3 sm:py-5"}`} data-testid="brand-row">
          <Link to="/" className="shrink-0" data-testid="nav-logo-link">
            <Logo compact={scrolled} />
          </Link>
          <div className="flex-1 min-w-0 text-center">
            <Link
              to="/"
              data-testid="brand-name"
              className={`font-display text-primary tracking-tight block transition-all duration-300 leading-tight ${scrolled ? "text-lg sm:text-2xl" : "text-2xl sm:text-4xl lg:text-5xl"}`}
            >
              {business.name || "Eileen Jenkins Florist"}
            </Link>
            <p
              className={`font-accent italic text-muted-foreground transition-all duration-300 overflow-hidden ${scrolled ? "max-h-0 opacity-0 mt-0" : "max-h-24 opacity-100 text-sm sm:text-xl mt-1 leading-normal pb-1"}`}
              data-testid="brand-tagline"
            >
              {business.tagline}
            </p>
          </div>
          {badge && (
            <img
              src={imgUrl(badge)}
              alt="We are in The Good Florist Guide 2025–2026"
              className={`shrink-0 object-contain transition-all duration-300 ${scrolled ? "h-9 w-9" : "h-12 w-12 sm:h-16 sm:w-16"}`}
              data-testid="gfg-badge-header"
            />
          )}
          <div className="flex items-center gap-1 shrink-0">
            <ThemeToggle />
            <Button variant="ghost" size="icon" className="rounded-full relative" onClick={() => navigate("/basket")} data-testid="basket-btn" aria-label="Basket">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="basket-count" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] px-1 h-[18px] flex items-center justify-center">
                  {count}
                </span>
              )}
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full lg:hidden" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto" data-testid="mobile-menu">
                <div className="mt-6 flex flex-col gap-0.5">
                  <button onClick={() => mobileGo("/")} className="text-left px-4 py-3 rounded-lg font-medium hover:bg-primary/10 hover:text-primary transition-colors" data-testid="mobile-nav-link-home">Home</button>
                  <button onClick={() => mobileGo("/shop")} className="text-left px-4 py-3 rounded-lg font-medium hover:bg-primary/10 hover:text-primary transition-colors" data-testid="mobile-nav-link-shop">Shop All Flowers</button>
                  {categoryTree.map((parent) => (
                    <div key={parent.id} className="mt-1" data-testid={`mobile-group-${parent.slug}`}>
                      <button
                        onClick={() => mobileGo(`/shop?category=${parent.slug}`)}
                        className="w-full text-left px-4 py-2.5 rounded-lg font-semibold text-sm uppercase tracking-wider text-primary hover:bg-primary/10 transition-colors"
                        data-testid={`mobile-nav-link-${parent.slug}`}
                      >
                        {parent.name}
                      </button>
                      <div className="ml-4 border-l pl-2">
                        {parent.children.map((child) => (
                          <button key={child.id} onClick={() => mobileGo(`/shop?category=${child.slug}`)} className="w-full text-left px-3 py-2 rounded-lg text-sm text-foreground/80 hover:bg-muted hover:text-primary transition-colors" data-testid={`mobile-nav-sub-${child.slug}`}>
                            {child.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t flex flex-col gap-0.5">
                    {[
                      ["Gallery", "/gallery", "gallery"],
                      ["About Us", "/about", "about"],
                      ["Delivery", "/delivery", "delivery"],
                      ["Testimonials", "/testimonials", "testimonials"],
                      ["FAQs", "/faqs", "faqs"],
                      ["Contact", "/contact", "contact"],
                    ].map(([label, to, id]) => (
                      <button key={to} onClick={() => mobileGo(to)} className="text-left px-4 py-3 rounded-lg font-medium hover:bg-primary/10 hover:text-primary transition-colors" data-testid={`mobile-nav-link-${id}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Nav row (desktop) */}
        <nav className="hidden lg:flex items-center justify-center gap-0.5 pb-2" aria-label="Main" data-testid="nav-row">
          <NavLink to="/" end data-testid="nav-link-home" className={simpleLinkCls}>Home</NavLink>
          <NavLink to="/shop" end data-testid="nav-link-shop" className={simpleLinkCls}>Shop</NavLink>
          {categoryTree.map((parent) => <CategoryDropdown key={parent.id} parent={parent} />)}
          <NavLink to="/gallery" data-testid="nav-link-gallery" className={simpleLinkCls}>Gallery</NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={simpleLinkCls}>About</NavLink>
          <NavLink to="/contact" data-testid="nav-link-contact" className={simpleLinkCls}>Contact</NavLink>
        </nav>
      </div>
    </header>
    <div aria-hidden="true" style={{ height: spacerH }} data-testid="navbar-spacer" />
    </>
  );
}
